import axios from "axios";

export const getPropertyTags = async (propertyId) => {
    try {
        const response = await axios.get(`/api/tags/properties/${propertyId}`);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || "Failed to fetch tags");
    }
};

export async function createTag({ name, userId, propertyId }) {
    const query = propertyId ? `?propertyId=${propertyId}` : "";
    const response = await axios.post(`/api/tags${query}`, {
        name,
        userId,
    });
    return response.data;
}

export const getTagVoteCounts = async (taggableId, tagId) => {
    try {
        const response = await axios.get(`/api/tags/${taggableId}/${tagId}/vote-counts`);
        return response.data;
    } catch (error) {
        throw error;
    }
};

export const getUserVote = async (taggableId, tagId, userId) => {
    try {
        const response = await axios.get(`/api/tags/${taggableId}/${tagId}/vote`, {
            params: { userId },
        });

        return response.data;
    } catch (error) {
        throw error;
    }
};

export const submitTagVote = async (taggableId, tagId, userId, voteType) => {
    try {
        const response = await axios.post(`/api/tags/${taggableId}/${tagId}/vote`, {
            userId,
            voteType,
        });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || "Failed to submit vote");
    }
};

/**
 * Get tag-specific comments for a property
 * @param {string} propertyId - The ID of the property
 * @param {string} tagId - The ID of the tag
 * @param {number} page - The page number to fetch
 * @param {number} limit - Number of items per page
 * @returns {Promise<Object>} A promise that resolves with the comments and pagination info
 */
export const getTagComments = async (propertyId, tagId, page = 1, limit = 10) => {
    try {
        const query = new URLSearchParams({
            page,
            limit,
        }).toString();
        const response = await axios.get(`/api/tags/properties/${propertyId}/comments/${tagId}?${query}`);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || "Failed to fetch tag comments");
    }
};

/**
 * Create a tag-specific comment
 * @param {string} userId - The ID of the user creating the comment
 * @param {string} propertyId - The ID of the property
 * @param {string} tagId - The ID of the tag
 * @param {string} content - The content of the comment
 * @param {string} [parentId] - Optional ID of the parent comment for replies
 * @returns {Promise<Object>} A promise that resolves with the created comment
 */
export const createTagComment = async (userId, propertyId, tagId, content, parentId = null) => {
    try {
        const data = {
            userId,
            content,
            parentId,
        };
        const response = await axios.post(`/api/tags/properties/${propertyId}/comments/${tagId}`, data);
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || "Failed to create tag comment");
    }
};

/**
 * Delete a tag-specific comment
 * @param {string} userId - The ID of the user who owns the comment
 * @param {string} propertyId - The ID of the property
 * @param {string} tagId - The ID of the tag
 * @param {string} commentId - The ID of the comment to delete
 * @returns {Promise<Object>} A promise that resolves with the server response
 */
export const deleteTagComment = async (userId, propertyId, tagId, commentId) => {
    try {
        const response = await axios.delete(
            `/api/tags/properties/${propertyId}/comments/${tagId}/${commentId}?userId=${userId}`
        );
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || "Failed to delete tag comment");
    }
};

/**
 * Delete a tag from a property
 * @param {string} userId - The ID of the user attempting to delete the tag
 * @param {string} propertyId - The ID of the property
 * @param {string} tagId - The ID of the tag to delete
 * @returns {Promise<Object>} A promise that resolves with the server response
 */
export const deleteTag = async (userId, propertyId, tagId) => {
    try {
        const response = await axios.delete(
            `/api/tags/properties/${propertyId}/${tagId}?userId=${userId}`
        );
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || "Failed to delete tag");
    }
};

/**
 * Get all comments for property tags
 * @param {string} propertyId - The ID of the property
 * @param {number} page - Page number for pagination
 * @param {number} limit - Number of items per page
 * @returns {Promise<Object>} The response containing comments data
 */
export const getPropertyTagsComments = async (propertyId, page = 1, limit = 10) => {
    try {
        const response = await axios.get(`/api/tags/properties/${propertyId}/comments`, {
            params: {
                page: Number(page),
                limit: Number(limit),
            },
        });
        return response.data;
    } catch (error) {
        throw new Error(error.response?.data?.message || "Failed to fetch property tags comments");
    }
};
